import { forwardRef, useCallback, useRef, useState, type DragEvent, type ReactNode, type Ref } from 'react';
import { getMimeType, isWithinSize } from '@repletoryreach/sdk-utils/file';
import type { BaseProps } from '../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../core/_internal/factory.js';
import './Dropzone.css';

export interface DropzoneProps extends BaseProps {
  /** Called with the accepted file array. */
  onDrop: (files: File[]) => void;
  /** Called when files were rejected (validation failed). */
  onReject?: (rejections: Array<{ file: File; reasons: string[] }>) => void;
  /** Accept filter: array of MIME types, file extensions (`'.pdf'`), or wildcards (`'image/*'`). */
  accept?: string[];
  /** Maximum file size in bytes. */
  maxSize?: number;
  /** Maximum total files in a single drop. */
  maxFiles?: number;
  /** Allow multiple files at once. Default true. */
  multiple?: boolean;
  /** Disable the dropzone. */
  disabled?: boolean;
  /** Loading state: shown when uploads are in flight. */
  loading?: boolean;
  /** Render either the static prompt OR a render-fn taking dropzone state. */
  children?: ReactNode | ((state: { accept: boolean; reject: boolean; idle: boolean; loading: boolean }) => ReactNode);
}

const ACCEPT_PRESETS = {
  IMAGE: ['image/*'],
  PDF: ['application/pdf'],
  CSV: ['text/csv', '.csv'],
  EXCEL: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xls', '.xlsx'],
  VIDEO: ['video/*'],
  AUDIO: ['audio/*'],
  /** WhatsApp-supported media (image/video/audio/document: common subset). */
  WHATSAPP: [
    'image/jpeg', 'image/png', 'image/webp',
    'video/mp4', 'video/3gpp',
    'audio/aac', 'audio/mp4', 'audio/mpeg', 'audio/amr', 'audio/ogg',
    'application/pdf', 'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain'
  ]
} as const;

function matchAccept(file: File, accept: string[]): boolean {
  if (accept.length === 0) return true;
  const mime = getMimeType(file);
  for (const a of accept) {
    if (a.startsWith('.')) {
      if (file.name.toLowerCase().endsWith(a.toLowerCase())) return true;
    } else if (a.endsWith('/*')) {
      if (mime.startsWith(a.slice(0, -1))) return true;
    } else {
      if (mime === a) return true;
    }
  }
  return false;
}

/** File dropzone with drag-and-drop + browse fallback. */
const DropzoneBase = forwardRef(function Dropzone(
  {
    onDrop, onReject,
    accept = [], maxSize, maxFiles, multiple = true,
    disabled, loading, children, ...rest
  }: DropzoneProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const [dragState, setDragState] = useState<'idle' | 'accept' | 'reject'>('idle');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const base = useBaseAttrs('rr-dropzone', { ...rest, disabled, loading }, [
    `rr-dropzone--${dragState}`,
    disabled && 'rr-dropzone--disabled',
    loading && 'rr-dropzone--loading'
  ]);
  const { dom } = splitBaseProps({ ...rest, disabled, loading });

  const validate = useCallback((files: File[]) => {
    const accepted: File[] = [];
    const rejected: Array<{ file: File; reasons: string[] }> = [];
    for (const file of files) {
      const reasons: string[] = [];
      if (accept.length && !matchAccept(file, accept)) reasons.push('file-invalid-type');
      if (maxSize && !isWithinSize(file, maxSize)) reasons.push('file-too-large');
      if (reasons.length) rejected.push({ file, reasons });
      else accepted.push(file);
    }
    if (maxFiles && accepted.length > maxFiles) {
      const overflow = accepted.splice(maxFiles).map((f) => ({ file: f, reasons: ['too-many-files'] }));
      rejected.push(...overflow);
    }
    return { accepted, rejected };
  }, [accept, maxSize, maxFiles]);

  const handleFiles = useCallback((fileList: FileList | null) => {
    if (!fileList) return;
    const files = Array.from(fileList);
    const { accepted, rejected } = validate(files);
    if (accepted.length) onDrop(multiple ? accepted : [accepted[0]]);
    if (rejected.length) onReject?.(rejected);
  }, [validate, onDrop, onReject, multiple]);

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (disabled || loading) return;
    const items = Array.from(e.dataTransfer.items ?? []);
    const files = items.filter((it) => it.kind === 'file');
    const allOk = files.every((it) => {
      const mime = it.type;
      return accept.length === 0 || accept.some((a) => a.endsWith('/*') ? mime.startsWith(a.slice(0, -1)) : mime === a || a.startsWith('.'));
    });
    setDragState(allOk ? 'accept' : 'reject');
  };
  const onDragLeave = () => setDragState('idle');
  const onDropHandler = (e: DragEvent) => {
    e.preventDefault();
    setDragState('idle');
    if (disabled || loading) return;
    handleFiles(e.dataTransfer.files);
  };

  const open = () => { if (!disabled && !loading) inputRef.current?.click(); };

  return (
    <div
      ref={ref}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDropHandler}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      {...base.attrs}
      className={base.className}
      style={base.style}
    >
      <input
        ref={inputRef}
        type="file"
        className="rr-sr-only"
        multiple={multiple}
        accept={accept.join(',')}
        disabled={disabled}
        onChange={(e) => {
          handleFiles(e.currentTarget.files);
          // Cleared, so choosing the same file again (after cancelling what it opened) is still a change.
          e.currentTarget.value = '';
        }}
        onClick={(e) => e.stopPropagation()}
      />
      {typeof children === 'function'
        ? children({ accept: dragState === 'accept', reject: dragState === 'reject', idle: dragState === 'idle', loading: !!loading })
        : (children ?? (
            <div className="rr-dropzone__default">
              <strong>Drop files here</strong>
              <span>or click to browse</span>
              {accept.length > 0 ? <small>{accept.join(', ')}</small> : null}
            </div>
          ))}
    </div>
  );
});

export const Dropzone = Object.assign(DropzoneBase, { ACCEPT: ACCEPT_PRESETS });
